import React, { useCallback, useEffect, useRef, useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Button } from "@/components/common/Button";
import { DropdownMenu } from "@/components/common/DropdownMenu";
import { TextInput } from "@/components/common/TextInput";

const XSMALL = { fontSize: "var(--font-size-x-small)" } as const;

type Placement = "bottom" | "bottom-end" | "right";

interface InlineComposerProps {
  /// Element the popover anchors to (its trigger).
  anchorRef: React.RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
  /// Placeholder for the single input.
  placeholder: string;
  /// Primary action button label.
  actionLabel: string;
  /// Runs with the trimmed value. Throw an `Error` to surface its message
  /// inline; resolving closes and clears the popover.
  onSubmit: (value: string) => Promise<void>;
  /// Optional hint shown under the input (until an error replaces it).
  helper?: string;
  placement?: Placement;
  /// Extra classes on the popover surface — e.g. a marker a parent dropdown can
  /// whitelist so opening this composer doesn't dismiss it.
  className?: string;
  /// Fixed popover width. Omit to inherit the anchor's width (only meaningful
  /// with `placement="bottom"`, which sizes to the anchor).
  width?: string;
}

/// The controlled popover body of the "Add related link" composer pattern: one
/// text input + a primary action, anchored to `anchorRef`. Enter submits, Esc
/// closes; a thrown `Error` surfaces its message inline. Owned open-state so it
/// can be opened from anywhere (a dropdown item, another button) — see
/// `InlineComposerButton` for the self-triggering form.
export const InlineComposer: React.FC<InlineComposerProps> = ({
  anchorRef,
  open,
  onClose,
  placeholder,
  actionLabel,
  onSubmit,
  helper,
  placement = "bottom",
  className,
  width,
}) => {
  const [value, setValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) bodyRef.current?.querySelector("input")?.focus();
    else {
      setValue("");
      setError(null);
    }
  }, [open]);

  const canSubmit = !!value.trim() && !submitting;

  const submit = useCallback(async () => {
    const v = value.trim();
    if (!v) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(v);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  }, [value, onSubmit, onClose]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.target as HTMLElement).tagName === "INPUT") {
      e.preventDefault();
      if (canSubmit) void submit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <DropdownMenu
      anchorRef={anchorRef}
      isOpen={open}
      onClose={onClose}
      placement={placement}
      className={`shadow-md ${className ?? ""}`}
      style={width ? { width } : undefined}
    >
      <div
        ref={bodyRef}
        onKeyDown={onKeyDown}
        className="flex flex-col gap-2 p-3"
      >
        <TextInput
          value={value}
          onChange={(v) => {
            setValue(v);
            if (error) setError(null);
          }}
          placeholder={placeholder}
        />
        <span
          style={{
            ...XSMALL,
            color: error ? "var(--color-error)" : "var(--color-fg-muted)",
          }}
        >
          {error ?? helper ?? "Enter to submit · Esc to close"}
        </span>
        <Button
          variant="primary"
          size="md"
          loading={submitting}
          disabled={!canSubmit}
          onClick={() => void submit()}
        >
          {actionLabel}
        </Button>
      </div>
    </DropdownMenu>
  );
};

interface InlineComposerButtonProps {
  /// Trigger button label.
  label: string;
  /// Placeholder for the single input.
  placeholder: string;
  /// Primary action button label inside the popover.
  actionLabel: string;
  /// Runs with the trimmed value. Throw an `Error` to surface its message
  /// inline; resolving closes and clears the popover.
  onSubmit: (value: string) => Promise<void>;
  /// Optional hint shown under the input (until an error replaces it).
  helper?: string;
  /// Optional leading icon on the trigger.
  icon?: React.ReactNode;
  disabled?: boolean;
  placement?: Placement;
}

/// A dashed trigger button that opens the `InlineComposer` popover — the
/// self-triggering form of the composer pattern (e.g. publishing a release
/// inline from a share dialog).
export const InlineComposerButton: React.FC<InlineComposerButtonProps> = ({
  label,
  placeholder,
  actionLabel,
  onSubmit,
  helper,
  icon,
  disabled,
  placement = "bottom",
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <div ref={anchorRef} className="inline-flex">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-1 shrink-0 min-h-[var(--control-height-sm)] px-2 py-0.5 rounded-app border border-dashed border-accent text-accent cursor-pointer hover:bg-accent-15 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
          style={{ fontSize: "var(--font-size-small)" }}
        >
          {icon}
          {label}
          <KeyboardArrowDownIcon className="icon-xs" />
        </button>
      </div>
      <InlineComposer
        anchorRef={anchorRef}
        open={open}
        onClose={() => setOpen(false)}
        placeholder={placeholder}
        actionLabel={actionLabel}
        onSubmit={onSubmit}
        helper={helper}
        placement={placement}
        width="300px"
      />
    </>
  );
};
